import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getAnnotationToolAdminProjectsPath } from '../../../../lib/routePaths';
import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { useUpdateProjectAllocationMutation } from '../../api/adminProjectsApiSlice';
import {
  setDefaultAllocationModeEdit,
  clearDefaultAllocationModeEdit,
} from '../../state/adminProjectSlice';
import {
  selectDefaultAllocationModeEdit,
  selectSupervisors,
  selectAnnotators,
} from '../../state/selector';

interface UseProjectAllocationResults {
  defaultAllocationMode: boolean;
  setAllocationModeDefault: () => void;
  setAllocationModeCustom: () => void;
  handleSaveAllocation: () => void;
  saveAllocationError: string | null;
  allFieldsFilled: boolean | null;
  isAllocationSaving: boolean;
}
export const useProjectAllocation = (): UseProjectAllocationResults => {
  const { projectId } = useParams();
  const dispatch = useAppDispatch();
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationModeEdit);
  const setAllocationModeDefault = () => {
    dispatch(setDefaultAllocationModeEdit());
  };
  const setAllocationModeCustom = () => {
    dispatch(clearDefaultAllocationModeEdit());
  };

  const selectedAnnotators = useAppSelector(selectAnnotators);
  const selectedSupervisors = useAppSelector(selectSupervisors);
  const [allFieldsFilled, setAllFieldsFilled] = useState<boolean | null>(null);
  useEffect(() => {
    const trainingTotal =
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.trainingPercentage ?? 0),
        0
      ) ?? 0;
    const testTotal =
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.testPercentage ?? 0),
        0
      ) ?? 0;
    const validationTotal =
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.validationPercentage ?? 0),
        0
      ) ?? 0;
    setAllFieldsFilled(
      Boolean(
        selectedAnnotators?.length &&
          selectedAnnotators?.length > 0 &&
          selectedSupervisors?.length &&
          selectedSupervisors?.length > 0 &&
          (trainingTotal === 100 || defaultAllocationMode) &&
          (testTotal === 100 || defaultAllocationMode) &&
          (validationTotal === 100 || defaultAllocationMode)
      )
    );
  }, [selectedAnnotators, selectedSupervisors, defaultAllocationMode]);

  const [saveAllocationError, setSaveAllocationError] = useState<string | null>(null);
  const [updateProjectAllocation, { isLoading: isUpdateProjectAllocationLoading }] =
    useUpdateProjectAllocationMutation();
  const navigate = useNavigate();
  const handleSaveAllocation = async () => {
    try {
      const response = await updateProjectAllocation({
        projectId: projectId as string,
        annotators: selectedAnnotators ?? [],
        supervisors: selectedSupervisors ?? [],
        defaultAllocationMode: defaultAllocationMode ?? false,
      });
      window.location.href = getAnnotationToolAdminProjectsPath();
    } catch (error) {
      setSaveAllocationError(
        'Failed to save allocation' + (error as { data: { message: string } }).data.message
      );
    }
  };

  return {
    defaultAllocationMode: defaultAllocationMode ?? false,
    setAllocationModeDefault,
    setAllocationModeCustom,
    handleSaveAllocation,
    saveAllocationError,
    allFieldsFilled,
    isAllocationSaving: isUpdateProjectAllocationLoading,
  };
};
